import { ArrowBack } from '@mui/icons-material'
import CircleIcon from '@mui/icons-material/Circle'
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material'
import { memo, useEffect, useRef, useState } from 'react'

interface ItineraryHeaderProps {
    onBackClick: () => void
    pageTitle: string
    route: string
    departureDate: string
    arrivalDate: string
    travellers: string
    isError?: boolean
    isRTL?: boolean
}

// Extracted styles to prevent re-creation on each render
const styles = {
    container: {
        width: '100%',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        px: 2,
        py: 1,
        position: 'fixed',
        top: 0,
        zIndex: 1000,
        minHeight: '70px'
    } as SxProps<Theme>,

    headerBox: {
        display: 'flex',
        alignItems: 'center',
        mb: 0.5,
        position: 'relative',
        px: 2
    } as SxProps<Theme>,

    backButton: {
        mr: 2,
        position: 'absolute',
        left: 0,
        top: '45%',
        p: 0.5,
        color: 'text.primary'
    } as SxProps<Theme>,

    title: {
        fontSize: 18,
        fontWeight: 700,
        color: 'text.primary',
        flex: 1,
        textAlign: 'center'
    } as SxProps<Theme>,

    detailsContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        mt: 0.5,
        px: 2
    } as SxProps<Theme>,

    detailText: {
        whiteSpace: 'nowrap',
        fontSize: 14,
        fontWeight: 400,
        color: 'text.secondary',
        textAlign: 'center'
    } as SxProps<Theme>,

    detailTextWithMargin: {
        whiteSpace: 'nowrap',
        ml: 0.4,
        fontSize: 14,
        fontWeight: 400,
        color: 'text.secondary',
        textAlign: 'center'
    } as SxProps<Theme>
} as const

const ItineraryHeader = ({
    onBackClick,
    pageTitle,
    route,
    departureDate,
    arrivalDate,
    travellers,
    isError,
    isRTL
}: ItineraryHeaderProps) => {
    const headerRef = useRef<HTMLDivElement | null>(null)
    const [spacerHeight, setSpacerHeight] = useState<number>(70)

    useEffect(() => {
        const update = () => {
            if (headerRef.current) {
                setSpacerHeight(headerRef.current.offsetHeight)
            }
        }

        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        const vv = (window as any).visualViewport as VisualViewport | undefined
        vv?.addEventListener('resize', update)

        return () => {
            window.removeEventListener('resize', update)
            window.removeEventListener('orientationchange', update)
            vv?.removeEventListener('resize', update)
        }
        // Recalculate if header content can change height
    }, [pageTitle, route, departureDate, arrivalDate, travellers, isError, isRTL])

    return (
        <Box>
            <Box sx={{ height: spacerHeight }} aria-hidden />
            <Box ref={headerRef} sx={styles.container}>
                {/* Header with back button and title */}
                <Box sx={styles.headerBox}>
                    <IconButton onClick={onBackClick} sx={styles.backButton} aria-label="Go back" size="small">
                        <ArrowBack sx={{ fontSize: 20, transform: isRTL ? 'scaleX(-1)' : 'none' }} />
                    </IconButton>

                    <Typography variant="h6" sx={styles.title}>
                        {pageTitle}
                    </Typography>
                </Box>

                {/* Flight details */}

                <Box sx={styles.detailsContainer}>
                    {!isError && (
                        <>
                            {route && <Typography sx={styles.detailText}>{route}</Typography>}

                            {departureDate && (
                                <Typography sx={styles.detailTextWithMargin}>
                                    <CircleIcon sx={{ fontSize: 6, color: 'grey.600', mx: '2px' }} /> {departureDate}{' '}
                                    {!!arrivalDate && `- ${arrivalDate}`}
                                </Typography>
                            )}

                            {travellers && (
                                <Typography sx={styles.detailTextWithMargin}>
                                    <CircleIcon sx={{ fontSize: 6, color: 'grey.600', mx: '2px' }} /> {travellers}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

ItineraryHeader.displayName = 'ItineraryHeader'
export default memo(ItineraryHeader)
